import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { authApi, setAuthToken } from './api/client';

const TOKEN_KEY = 'attractis_token';

export type Business = {
  id: string;
  name: string;
  slug: string;
  role: string;
};

type User = {
  id: string | number;
  email: string;
  role?: string;
  firstName?: string;
  lastName?: string;
} | null;

type AuthContextType = {
  user: User;
  token: string | null;
  /** Non-null while the user is authenticated but hasn't selected a business yet. */
  pendingBusinesses: Business[] | null;
  selectedBusiness: Business | null;
  login: (email: string, password: string) => Promise<void>;
  selectBusiness: (business: Business) => Promise<void>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType>({} as any);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState<User>(null);
  const [pendingBusinesses, setPendingBusinesses] = useState<Business[] | null>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);

  // Holds the plain (non-scoped) login token while the user picks a business.
  const loginTokenRef = useRef<string>('');

  useEffect(() => {
    setAuthToken(token);
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  }, [token]);

  const selectBusiness = useCallback(async (business: Business) => {
    const plainToken = loginTokenRef.current;
    if (!plainToken) throw new Error('No login token available');

    const res = await authApi.post(
      '/auth/select-business',
      { businessId: business.id },
      { headers: { Authorization: `Bearer ${plainToken}` } }
    );
    const scopedToken: string = res.data.token ?? plainToken;
    loginTokenRef.current = '';
    setToken(scopedToken);
    setSelectedBusiness(business);
    setPendingBusinesses(null);
  }, []);

  const value = useMemo<AuthContextType>(() => ({
    user,
    token,
    pendingBusinesses,
    selectedBusiness,
    login: async (email, password) => {
      const loginData = await authApi.post('/auth/login', { email, password });
      const plainToken: string = loginData.data.token;
      const loginUser: any = loginData.data.user;
      const businesses: Business[] = loginData.data.businesses ?? [];

      setUser(loginUser);

      if (businesses.length === 0) {
        // No businesses associated — store plain token and let backend return 403
        setToken(plainToken);
        return;
      }

      if (businesses.length === 1) {
        // Auto-select the only business
        loginTokenRef.current = plainToken;
        await selectBusiness(businesses[0]!);
        return;
      }

      // Multiple businesses — present picker
      loginTokenRef.current = plainToken;
      setPendingBusinesses(businesses);
    },
    selectBusiness,
    logout: () => {
      loginTokenRef.current = '';
      setToken(null);
      setUser(null);
      setPendingBusinesses(null);
      setSelectedBusiness(null);
    },
  }), [user, token, pendingBusinesses, selectedBusiness, selectBusiness]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() { return useContext(AuthContext); }
